import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { buttonClass } from "@/components/common/controls";
import { SectionCard } from "@/components/posting/parts";
import type { PostingKeyword } from "@/lib/api/catalogue";
import { actionProps } from "@/lib/controlAnalytics";
import type { ToolFit } from "@/lib/tools/check";
import CheckResult, { WaitingKeywords } from "./CheckResult";
import TailorBlock, { PrivacyNote, type TailorSlot } from "./TailorBlock";

const LET_AI = "Let AI tailor it for this job";
const ABOUT_AI = "AI tailoring is what Job Envoy does";
const WHAT_AI_DOES =
    "AI tailoring is what Job Envoy does. It rewrites your resume around experience you really have. It does not fill gaps.";
const NO_FORM = "With this job, Tailor needs no form.";
const JOB_CLOSED =
    "This job has closed, so there is nothing to tailor for it. For a job that is open, AI rewrites your resume around experience you really have. It does not fill gaps.";

/** The widths of the empty chips, as a result's would fall. */
const GHOSTS = ["w-28", "w-20", "w-36", "w-16", "w-24", "w-32", "w-20", "w-28"];

function Ghosts() {
    return (
        <div aria-hidden="true" className="flex flex-wrap gap-1.5">
            {GHOSTS.map((width, index) => (
                <span
                    key={index}
                    className={`h-[24px] rounded-full ring-1 ring-inset ring-line-strong ${width}`}
                />
            ))}
        </div>
    );
}

function Rule() {
    return <div className="h-px bg-line-soft" />;
}

function Problem({ text }: { text: string | null }) {
    if (text === null) return null;
    return (
        <p role="alert" className="m-0 text-ui font-medium text-negative [text-wrap:pretty]">
            {text}
        </p>
    );
}

function CheckButton({ checking, onCheck }: { checking: boolean; onCheck: () => void }) {
    return (
        <button
            {...actionProps("tool_check_clicked", { source: "ResumeCheck" })}
            type="button"
            disabled={checking}
            onClick={onCheck}
            className={buttonClass("primary", "tall", "w-full")}
        >
            {checking ? "Checking" : "Check my resume"}
        </button>
    );
}

/** A result's heading, which takes the cursor when the Check button goes. */
function ResultHeading({
    heading,
    children,
}: {
    heading: RefObject<HTMLHeadingElement | null>;
    children: ReactNode;
}) {
    return (
        <h2
            ref={heading}
            tabIndex={-1}
            className="m-0 scroll-mt-[148px] font-display text-card-title font-bold tracking-[-0.01em] outline-none"
        >
            {children}
        </h2>
    );
}

/**
 * Moves the cursor to the heading when a check's result arrives. The
 * Check button has just gone, so the cursor would fall to the page's
 * start. Someone who moved on to a box while it ran keeps their place.
 */
function useResultFocus(hasResult: boolean): RefObject<HTMLHeadingElement | null> {
    const heading = useRef<HTMLHeadingElement>(null);
    const had = useRef(hasResult);
    useEffect(() => {
        const arrived = hasResult && !had.current;
        had.current = hasResult;
        const adrift = !document.activeElement || document.activeElement === document.body;
        if (arrived && adrift) heading.current?.focus();
    }, [hasResult]);
    return heading;
}

/** What the polite region says: the count, or that there is none to give. */
function resultWords(fit: ToolFit): string {
    return fit.asked === 0
        ? "No skills to count. This job names no skill we know."
        : `${fit.covered} of ${fit.asked} keywords your resume says`;
}

/**
 * The page's one place for the result, the buttons and the promises. It
 * sits beside the inputs and stays in view while they scroll, and scrolls
 * by itself where it is taller than the screen, so Tailor is always in
 * reach. The polite region is always there, empty, because a screen reader
 * speaks a change to a region it already knows, and not one that arrives
 * with its words.
 */
export default function CheckPanel({
    fit,
    keywords,
    settled,
    waiting,
    problem,
    checking,
    visitor,
    member,
    status,
    onCheck,
    tailor,
}: {
    fit: ToolFit | null;
    /** The keywords a check found: null before one. */
    keywords: PostingKeyword[] | null;
    /** There is a result to show, the count or that there is none. */
    settled: boolean;
    /** A posting's keywords: null when the job is pasted. */
    waiting: PostingKeyword[] | null;
    problem: string | null;
    checking: boolean;
    visitor: boolean;
    member: boolean;
    /** Where a tailor stands, over the result. */
    status: ReactNode;
    onCheck: () => void;
    /** Null on a closed job, which offers no Tailor. */
    tailor: TailorSlot;
}) {
    const heading = useResultFocus(keywords !== null);
    const posting = waiting !== null;
    const closed = tailor === null;
    const check = <CheckButton checking={checking} onCheck={onCheck} />;
    const note = <PrivacyNote posting={posting} closed={closed} member={member} />;
    // A member's Tailor says whose resume it reads under its button, so a result needs no note.
    const resultNote = member && tailor ? null : note;
    const tailorLine = posting ? `${WHAT_AI_DOES} ${NO_FORM}` : WHAT_AI_DOES;
    const card = "gap-4 min-[1024px]:max-h-[calc(100dvh-104px)] min-[1024px]:overflow-y-auto";
    /** Tailor's part of a view: `offer` once there is a result to act on. */
    const tailorBlock = (line: string, offer: boolean) =>
        tailor ? (
            <TailorBlock
                title={LET_AI}
                line={line}
                visitor={visitor}
                member={member}
                tailor={offer ? tailor : undefined}
            />
        ) : (
            <TailorBlock title={ABOUT_AI} line={JOB_CLOSED} visitor={false} member={member} />
        );

    return (
        <div
            data-testid="check-panel"
            className="flex min-w-0 flex-col gap-4 [overflow-wrap:anywhere] min-[1024px]:sticky min-[1024px]:top-[84px]"
        >
            <div role="status" aria-live="polite" className="sr-only">
                {settled && fit ? resultWords(fit) : ""}
            </div>
            {status}
            {fit?.asked === 0 ? (
                <SectionCard className={card}>
                    <ResultHeading heading={heading}>No skills to count</ResultHeading>
                    <Problem text={problem} />
                    <p className="m-0 text-ui text-fg-muted [text-wrap:pretty]">
                        This job names no skill we know, so there is nothing to count. That says
                        nothing about your resume.
                    </p>
                    <Rule />
                    {tailorBlock(
                        `A tailor reads the whole job, so it can still help. ${tailorLine}`,
                        settled,
                    )}
                    {settled ? resultNote : note}
                </SectionCard>
            ) : fit && keywords ? (
                <SectionCard className={card}>
                    <ResultHeading heading={heading}>What your resume is missing</ResultHeading>
                    <Problem text={problem} />
                    <CheckResult fit={fit} />
                    <Rule />
                    {tailorBlock(tailorLine, true)}
                    {resultNote}
                </SectionCard>
            ) : fit && waiting ? (
                <SectionCard
                    className={card}
                    title="What this job asks for"
                    caption="Each keyword turns green when your resume names it."
                >
                    <WaitingKeywords keywords={waiting} fit={fit} />
                    <Problem text={problem} />
                    {check}
                    {note}
                    <Rule />
                    {tailorBlock(tailorLine, false)}
                </SectionCard>
            ) : (
                <SectionCard
                    className={card}
                    title="What your resume is missing"
                    caption="Your result shows here and stays in view while you edit."
                >
                    <Ghosts />
                    <Problem text={problem} />
                    {check}
                    {note}
                    <Rule />
                    <TailorBlock
                        title={ABOUT_AI}
                        line="See the gap first. Then AI rewrites your resume for this job, around experience you really have. It does not fill gaps."
                        visitor={visitor}
                        member={member}
                    />
                </SectionCard>
            )}
        </div>
    );
}
